
import React, { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Plus, Loader2, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { addPagePricingMetric, getPricingMetrics, createPricingMetric } from "@/utils/metricsManagementHelper";

type Modality = "Flat" | "License" | "Credit" | "Usage" | "Threshold" | "Unknown" | null;

interface PricingMetric {
  id: string;
  name: string;
  subcategory: string | null;
  modality: Modality;
}

interface AddMetricDialogProps {
  slug: string;
  onMetricAdded: () => void;
}

// Unclassified pricing metric ID
const UNCLASSIFIED_METRIC_ID = "99db187b-65c1-4ea7-83a5-f7afce82cb43";

const AddMetricDialog = ({ slug, onMetricAdded }: AddMetricDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [selectedMetricId, setSelectedMetricId] = useState('');
  const [sampleValue, setSampleValue] = useState('');
  const [sampleValues, setSampleValues] = useState<string[]>([]);
  const [modality, setModality] = useState<Modality>(null);
  const [pricingMetrics, setPricingMetrics] = useState<PricingMetric[]>([]);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreatingMetric, setIsCreatingMetric] = useState(false);
  const [newMetricName, setNewMetricName] = useState('');
  const [newMetricSubcategory, setNewMetricSubcategory] = useState('');
  const [newMetricModality, setNewMetricModality] = useState<Modality>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      loadPricingMetrics();
    }
  }, [isOpen]);

  const loadPricingMetrics = async () => {
    setIsLoadingMetrics(true);
    try {
      const metrics = await getPricingMetrics();
      // Convert the API response to match our PricingMetric type
      const typedMetrics: PricingMetric[] = metrics.map(metric => ({
        id: metric.id,
        name: metric.name,
        subcategory: metric.subcategory,
        // Cast the string modality to our Modality type, or null if it doesn't match any valid value
        modality: (metric.modality as Modality) || null
      }));
      setPricingMetrics(typedMetrics);
    } catch (error) {
      console.error("Error loading pricing metrics:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load pricing metrics."
      });
    } finally {
      setIsLoadingMetrics(false);
    }
  };

  const handleAddSampleValue = () => {
    if (sampleValue.trim()) {
      setSampleValues([...sampleValues, sampleValue.trim()]);
      setSampleValue('');
    }
  };

  const handleRemoveSampleValue = (indexToRemove: number) => {
    setSampleValues(sampleValues.filter((_, index) => index !== indexToRemove));
  };

  const handleCreateNewMetric = async () => {
    if (!newMetricName.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please enter a name for the new metric."
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await createPricingMetric(
        newMetricName,
        newMetricSubcategory || null,
        newMetricModality
      );

      if (result) {
        toast({
          title: "Success",
          description: "New pricing metric created successfully."
        });
        
        // Update the dropdown list with the new metric
        await loadPricingMetrics();
        
        // Select the newly created metric
        setSelectedMetricId(result.id);
        
        // Reset the create new metric form
        setIsCreatingMetric(false);
        setNewMetricName('');
        setNewMetricSubcategory('');
        setNewMetricModality(null);
      }
    } catch (error) {
      console.error("Error creating new metric:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create new pricing metric."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!displayName.trim()) {
      toast({
        variant: "destructive",
        title: "Validation Error",
        description: "Please enter a display name for the metric."
      });
      return;
    }

    // Use the selected metric ID or the Unclassified ID if none selected
    const metricIdToUse = selectedMetricId || UNCLASSIFIED_METRIC_ID;

    setIsLoading(true);
    try {
      const result = await addPagePricingMetric(
        slug,
        displayName,
        metricIdToUse,
        sampleValues.length > 0 ? sampleValues : undefined,
        modality
      );

      if (result) {
        toast({
          title: "Success",
          description: "Metric added successfully."
        });
        setIsOpen(false);
        onMetricAdded();
        
        // Clear form
        setDisplayName('');
        setSelectedMetricId('');
        setSampleValue('');
        setSampleValues([]);
        setModality(null);
      }
    } catch (error) {
      console.error("Error adding metric:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add metric."
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Filter metrics based on search term
  const filteredMetrics = pricingMetrics.filter(metric => 
    metric.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (metric.subcategory && metric.subcategory.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="default" className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Metric
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add New Metric</DialogTitle>
          <DialogDescription>
            Add a new pricing metric for {slug}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-2">
            <label htmlFor="displayName" className="text-sm font-medium col-span-4">
               Name
            </label>
            <Input
              id="displayName"
              className="col-span-4"
              placeholder="e.g. API Calls per Month"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          
          {isCreatingMetric ? (
            <div className="border p-4 rounded-md bg-gray-50 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">Create New Pricing Metric</h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setIsCreatingMetric(false)}
                >
                  Cancel
                </Button>
              </div>
              
              <div className="grid gap-2">
                <label htmlFor="newMetricName" className="text-sm font-medium">
                  Metric Name
                </label>
                <Input
                  id="newMetricName"
                  placeholder="e.g. API Calls"
                  value={newMetricName}
                  onChange={(e) => setNewMetricName(e.target.value)}
                />
              </div>
              
              <div className="grid gap-2">
                <label htmlFor="newMetricSubcategory" className="text-sm font-medium">
                  Subcategory (Optional)
                </label>
                <Input
                  id="newMetricSubcategory"
                  placeholder="e.g. Basic Tier"
                  value={newMetricSubcategory}
                  onChange={(e) => setNewMetricSubcategory(e.target.value)}
                />
              </div>
              
              <div className="grid gap-2">
                <label htmlFor="newMetricModality" className="text-sm font-medium">
                  Modality (Optional)
                </label>
                <Select value={newMetricModality || "none"} onValueChange={(value) => setNewMetricModality(value === "none" ? null : value as Modality)}>
                  <SelectTrigger id="newMetricModality">
                    <SelectValue placeholder="Select modality" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not specified</SelectItem>
                    <SelectItem value="Flat">Flat</SelectItem>
                    <SelectItem value="License">License</SelectItem>
                    <SelectItem value="Credit">Credit</SelectItem>
                    <SelectItem value="Usage">Usage</SelectItem>
                    <SelectItem value="Threshold">Threshold</SelectItem>
                    <SelectItem value="Unknown">Unknown</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                onClick={handleCreateNewMetric} 
                disabled={isLoading || !newMetricName.trim()}
                className="w-full"
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Metric
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-4 items-center gap-2">
              <label htmlFor="pricingMetric" className="text-sm font-medium col-span-4">
                Pricing Metric <span className="text-gray-500 text-xs">(Optional - will use "Unclassified" if not selected)</span>
              </label>
              <div className="col-span-4 space-y-2">
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search metrics..."
                    className="pl-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Select value={selectedMetricId} onValueChange={setSelectedMetricId}>
                  <SelectTrigger id="pricingMetric" className="w-full">
                    <SelectValue placeholder="Select a pricing metric (optional)" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {isLoadingMetrics ? (
                      <div className="flex items-center justify-center p-2">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        <span>Loading metrics...</span>
                      </div>
                    ) : filteredMetrics.length === 0 && searchTerm ? (
                      <div className="p-2">
                        <p className="text-sm text-gray-500 mb-2">No metrics found</p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full"
                          onClick={() => {
                            setIsCreatingMetric(true);
                            setNewMetricName(searchTerm);
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Create "{searchTerm}"
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="p-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="w-full"
                            onClick={() => setIsCreatingMetric(true)}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Create New Metric
                          </Button>
                        </div>
                        {filteredMetrics.map((metric) => (
                          <SelectItem key={metric.id} value={metric.id || "default-id"}>
                            {metric.name} {metric.subcategory ? `(${metric.subcategory})` : ''}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <div className="grid grid-cols-4 items-center gap-2">
            <label htmlFor="modality" className="text-sm font-medium col-span-4">
              Modality (Optional)
            </label>
            <Select value={modality || "none"} onValueChange={(value) => setModality(value === "none" ? null : value as Modality)}>
              <SelectTrigger id="modality" className="col-span-4">
                <SelectValue placeholder="Select modality (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Not specified</SelectItem>
                <SelectItem value="Flat">Flat</SelectItem>
                <SelectItem value="License">License</SelectItem>
                <SelectItem value="Credit">Credit</SelectItem>
                <SelectItem value="Usage">Usage</SelectItem>
                <SelectItem value="Threshold">Threshold</SelectItem>
                <SelectItem value="Unknown">Unknown</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-2">
            <label htmlFor="sampleValue" className="text-sm font-medium col-span-4">
              Sample Values (Optional)
            </label>
            <div className="col-span-3">
              <Input
                id="sampleValue"
                placeholder="Add a sample value"
                value={sampleValue}
                onChange={(e) => setSampleValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddSampleValue();
                  }
                }}
              />
            </div>
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleAddSampleValue}
              className="col-span-1"
            >
              Add
            </Button>
          </div>
          
          {sampleValues.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {sampleValues.map((value, index) => (
                <div 
                  key={index} 
                  className="bg-slate-100 text-slate-800 text-xs px-2 py-1 rounded flex items-center gap-1"
                >
                  {value}
                  <button 
                    type="button" 
                    onClick={() => handleRemoveSampleValue(index)}
                    className="ml-1 text-slate-500 hover:text-slate-800"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Add Metric
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddMetricDialog;
